// @ts-strict-ignore
import { relativeTimingLabel, tooltipDisplayLifecycleEventName } from '@/helpers/displayHelpers';
import { RootState } from '@/reducers/reducers';
import {
    selectDestinationInfoByDestinationStepConfigId,
    selectIsWorkflowsMigratedAndEnabled,
    selectStepConfigDataFromStepGraphs,
    selectWorkflowEnabledSteps,
} from '@/selectors/selectors';
import { LifecycleEventType, RelativeTiming, ReleaseStepType } from '@/types';
import { Nullable } from '@/typescriptHelpers';
import React from 'react';
import { useSelector } from 'react-redux';

enum DestinationSelector {
    ANY = 'Any',
    ALL = 'All',
}

// Keep a stable empty fallback for org-level usage so selector results don't
// change identity on every render and retrigger form initialization effects.
const EMPTY_OBJECT = {};

const createOptionForEvent = (event: LifecycleEventType, destinationSelector?: DestinationSelector) => {
    if (!destinationSelector) {
        return { value: event, label: tooltipDisplayLifecycleEventName(event) };
    }

    return {
        value: `${event}:${destinationSelector}`,
        label: `${tooltipDisplayLifecycleEventName(event)} (${destinationSelector})`,
    };
};

export const useRelativeTimeTriggerOptions = (appId: Nullable<string>, stepConfigId: Nullable<string>) => {
    const isWorkflowsEnabled = useSelector((state: RootState) =>
        appId ? selectIsWorkflowsMigratedAndEnabled(state, appId) : false,
    );
    const workflowEnabledSteps = useSelector((state: RootState) =>
        appId ? selectWorkflowEnabledSteps(state, appId) : EMPTY_OBJECT,
    );
    const stepGraphStepsMap = useSelector((state: RootState) =>
        appId ? selectStepConfigDataFromStepGraphs(state, appId) : null,
    );
    const destinationStepConfigIds =
        stepGraphStepsMap ? (stepGraphStepsMap[stepConfigId]?.destinationStepConfigIds ?? []) : [];
    const destinationsByStepConfigId = useSelector((state: RootState) =>
        appId ? selectDestinationInfoByDestinationStepConfigId(state, appId) : EMPTY_OBJECT,
    );

    const destinations = React.useMemo(() => {
        return destinationStepConfigIds.map((d) => destinationsByStepConfigId[d]).filter((d) => !!d);
    }, [destinationStepConfigIds, destinationsByStepConfigId]);

    const isWorkflowsEnabledAndMultiDestination = React.useMemo(() => {
        return isWorkflowsEnabled && destinations.length > 1;
    }, [isWorkflowsEnabled, destinations]);

    const relativeTimingOptions = Object.values(RelativeTiming).map((value: RelativeTiming) => ({
        value,
        label: relativeTimingLabel(value),
    }));

    const lifecycleEventOptions = React.useMemo(() => {
        const options = [createOptionForEvent(LifecycleEventType.LifecycleEventTypeKickoff)];

        if (isWorkflowsEnabledAndMultiDestination && workflowEnabledSteps[ReleaseStepType.Submission]) {
            options.push(createOptionForEvent(LifecycleEventType.LifecycleEventTypeSubmit, DestinationSelector.ANY));
            options.push(createOptionForEvent(LifecycleEventType.LifecycleEventTypeSubmit, DestinationSelector.ALL));
        } else {
            options.push(createOptionForEvent(LifecycleEventType.LifecycleEventTypeSubmit));
        }

        if (isWorkflowsEnabledAndMultiDestination && workflowEnabledSteps[ReleaseStepType.Submission]) {
            options.push(createOptionForEvent(LifecycleEventType.LifecycleEventTypeRelease, DestinationSelector.ANY));
            options.push(createOptionForEvent(LifecycleEventType.LifecycleEventTypeRelease, DestinationSelector.ALL));
        } else {
            options.push(createOptionForEvent(LifecycleEventType.LifecycleEventTypeRelease));
        }

        if (isWorkflowsEnabledAndMultiDestination && workflowEnabledSteps[ReleaseStepType.Rollout]) {
            options.push(
                createOptionForEvent(LifecycleEventType.LifecycleEventTypeRolloutCompletion, DestinationSelector.ANY),
            );
            options.push(
                createOptionForEvent(LifecycleEventType.LifecycleEventTypeRolloutCompletion, DestinationSelector.ALL),
            );
        } else {
            options.push(createOptionForEvent(LifecycleEventType.LifecycleEventTypeRolloutCompletion));
        }

        return options;
    }, [isWorkflowsEnabledAndMultiDestination, workflowEnabledSteps]);

    const parseSelectedLifecycleEventOption = React.useCallback((value: string) => {
        const [lifecycleEventType, waitForAllDestinations] = value.split(':');
        return {
            lifecycleEventType: lifecycleEventType as LifecycleEventType,
            waitForAllDestinations: !waitForAllDestinations || waitForAllDestinations === DestinationSelector.ALL,
        };
    }, []);

    const convertToSelectedLifecycleEventValue = React.useCallback(
        (eventType: LifecycleEventType, waitForAllDestinations: boolean) => {
            const selector = waitForAllDestinations ? DestinationSelector.ALL : DestinationSelector.ANY;
            const { value } = createOptionForEvent(eventType, selector);
            return lifecycleEventOptions.find((option) => option.value === value) ? value : eventType;
        },
        [lifecycleEventOptions],
    );

    return {
        relativeTimingOptions,
        lifecycleEventOptions,
        parseSelectedLifecycleEventOption,
        convertToSelectedLifecycleEventValue,
    };
};
